import type { Language } from './languages.js';

export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

/**
 * Function-signature metadata for a problem.
 * The judge uses this + the user's code to generate the stdin/stdout harness.
 *
 * MVP supports only primitive args and arrays thereof. Later phases add
 * linked lists, trees, matrices.
 */
export interface ProblemSignature {
  functionName: string; // e.g. "twoSum"
  params: ProblemParam[];
  returnType: ProblemType;
}

export interface ProblemParam {
  name: string;
  type: ProblemType;
}

export type ProblemType =
  | 'int'
  | 'long'
  | 'double'
  | 'bool'
  | 'string'
  | 'int[]'
  | 'long[]'
  | 'double[]'
  | 'bool[]'
  | 'string[]';

/**
 * Public-safe problem summary for the problem list page.
 */
export interface ProblemSummary {
  id: string;
  slug: string;
  title: string;
  difficulty: Difficulty;
  tags: string[];
  // null unless the user is authenticated; indicates whether they've solved this problem.
  solvedByUser?: boolean;
}

/**
 * Full problem detail for the solve page.
 */
export interface ProblemDetail extends ProblemSummary {
  descriptionMarkdown: string;
  signature: ProblemSignature;
  defaultCode: Partial<Record<Language, string>>;
  sampleCases: ProblemSampleCase[];
}

export interface ProblemSampleCase {
  input: string;
  expected: string;
  explanation?: string;
}
